/**
 * A UUID, in a browser or in Node.
 *
 * `node:crypto` used to be imported directly for this, which was the single
 * thing keeping the conversation runner off the browser — and the runner has
 * to run there, because a server that opens conversations on a caller's behalf
 * cannot prove the caller may touch the channel they named (see
 * `routes/run.ts`). One import was the whole boundary.
 *
 * `globalThis.crypto` is standard in browsers and in Node 19+, so it is the
 * normal path both sides. The fallback exists for a browser page served over
 * plain HTTP, where `crypto.randomUUID` is undefined because the context is
 * not secure while `getRandomValues` still works.
 */
export const uuid = (): string => {
  const webCrypto = globalThis.crypto;

  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  // RFC 4122 v4 from real random bytes: set the version (4) and variant bits,
  // then render. Only reached on an insecure origin.
  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join(''),
    ].join('-');
  }

  throw new Error(
    'No Web Crypto available: a message id cannot be generated. ' +
      'In a browser this means an insecure context; in Node it means a ' +
      'runtime older than 19.'
  );
};
